import { useEffect, useMemo, useState } from "react";
import { api } from "./adminApi";

const API = process.env.REACT_APP_API_URL;

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = () => setMatches(m.matches);
    m.addEventListener ? m.addEventListener("change", onChange) : m.addListener(onChange);
    return () => (m.removeEventListener ? m.removeEventListener("change", onChange) : m.removeListener(onChange));
  }, [query]);
  return matches;
}

export default function AdminMedia() {
  const isMobile = useMediaQuery("(max-width: 940px)");

  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  const [mediaType, setMediaType] = useState("image");
  const [titleEn, setTitleEn] = useState("");
  const [titleKn, setTitleKn] = useState("");
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);

  const [loading, setLoading] = useState(false);

  // Pagination (client-side)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, total: rows.length };
    for (const r of rows) {
      if (r.status === "approved") c.approved += 1;
      else if (r.status === "rejected") c.rejected += 1;
      else c.pending += 1;
    }
    return c;
  }, [rows]);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(rows.length / pageSize)), [rows.length, pageSize]);
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (rows || []).slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
    if (page < 1) setPage(1);
  }, [page, pageCount]);

  async function load() {
    setErr("");
    setLoading(true);
    try {
      const res = await api.get("/api/admin/media");
      setRows(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load media");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function upload(e) {
    e.preventDefault();
    setErr("");

    try {
      const fd = new FormData();
      fd.append("media_type", mediaType);
      fd.append("title_en", titleEn);
      fd.append("title_kn", titleKn);
      if (file) fd.append("file", file);

      await api.post("/api/admin/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setTitleEn("");
      setTitleKn("");
      setFile(null);
      setFileKey((k) => k + 1);
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.message || "Upload failed");
    }
  }

  async function approve(id) {
    setErr("");
    try {
      await api.post(`/api/admin/media/${id}/approve`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || "Approve failed");
    }
  }

  async function reject(id) {
    setErr("");
    try {
      await api.post(`/api/admin/media/${id}/reject`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || "Reject failed");
    }
  }

  // ✅ NEW: Delete single (backend will also delete file from /uploads/media)
  async function deleteOne(id) {
    const ok = window.confirm("Delete this media item permanently? This will also remove the uploaded file.");
    if (!ok) return;

    setErr("");
    try {
      await api.delete(`/api/admin/media/${id}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || "Delete failed");
    }
  }

  // ✅ NEW: Delete All for selected type (image/video/audio)
  async function deleteAllByType() {
    const ok = window.confirm(
      `Delete ALL ${mediaType.toUpperCase()} items?\n\nThis will also delete their uploaded files.\nThis cannot be undone.`
    );
    if (!ok) return;

    setErr("");
    try {
      await api.delete(`/api/admin/media?type=${encodeURIComponent(mediaType)}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || "Delete all failed");
    }
  }

  function mediaUrl(r) {
    if (!r?.media_url) return "";
    // Keep same behavior, but safer if API is not set
    if (API) return `${API}${r.media_url}`;
    return r.media_url;
  }

  function renderPreview(r) {
    const url = mediaUrl(r);
    if (!url) return <span style={styles.muted}>—</span>;

    if (r.media_type === "image") {
      return <img src={url} alt={r.title_en || "media"} style={styles.previewImg} loading="lazy" />;
    }

    if (r.media_type === "video") {
      return (
        <video style={styles.previewVideo} muted controls={false}>
          <source src={url} />
        </video>
      );
    }

    return (
      <audio controls style={styles.previewAudio}>
        <source src={url} />
      </audio>
    );
  }

  return (
    <div style={styles.page}>
      <Header
        title="Media Gallery"
        subtitle="Upload images/videos/audio and approve or reject pending uploads. Responsive layout for all devices."
        right={
          <>
            <button onClick={load} style={styles.ghostBtn} title="Refresh">
              ⟳ Refresh
            </button>
            {/* ✅ NEW */}
            <button onClick={deleteAllByType} style={styles.dangerBtn} title="Delete all by type">
              🗑 Delete All ({mediaType})
            </button>
          </>
        }
      />

      {/* KPI strip */}
      <div style={styles.kpiRow}>
        <Kpi tone="primary" label="Total" value={counts.total} />
        <Kpi tone="success" label="Approved" value={counts.approved} />
        <Kpi tone="pending" label="Pending" value={counts.pending} />
        <Kpi tone="danger" label="Rejected" value={counts.rejected} />
      </div>

      <div
        style={{
          ...styles.grid,
          gridTemplateColumns: isMobile ? "1fr" : "420px 1fr",
        }}
      >
        {/* Upload */}
        <div style={styles.card}>
          <div style={styles.cardHead}>
            <div>
              <div style={styles.cardTitle}>Upload Media</div>
              <div style={styles.cardSub}>Choose type, title, and upload file.</div>
            </div>
            <Pill tone="pending">Pending: {counts.pending}</Pill>
          </div>

          <div style={styles.cardBody}>
            <form onSubmit={upload} style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={styles.label}>Media Type</div>
                <select value={mediaType} onChange={(e) => setMediaType(e.target.value)} style={styles.select}>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="audio">Audio</option>
                </select>
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <div style={styles.label}>Title (English)</div>
                <input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} placeholder="Title (English)" style={styles.input} />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <div style={styles.label}>Title (Kannada)</div>
                <input value={titleKn} onChange={(e) => setTitleKn(e.target.value)} placeholder="Title (Kannada)" style={styles.input} />
              </div>

              <div style={styles.drop}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 950, color: "#0f172a" }}>Select File</div>
                    <div style={styles.muted}>
                      Accepted:{" "}
                      <span style={styles.mono}>
                        {mediaType === "image" ? "image/*" : mediaType === "video" ? "video/*" : "audio/*"}
                      </span>
                    </div>
                  </div>
                  <Pill tone="primary">{mediaType.toUpperCase()}</Pill>
                </div>

                <input
                  key={fileKey}
                  type="file"
                  accept={mediaType === "image" ? "image/*" : mediaType === "video" ? "video/*" : "audio/*"}
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  style={styles.file}
                />

                {file ? (
                  <div style={styles.fileMeta}>
                    <span style={{ fontWeight: 950, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {file.name}
                    </span>
                    <span style={styles.muted}>{Math.round(file.size / 1024)} KB</span>
                  </div>
                ) : (
                  <div style={styles.muted}>No file selected.</div>
                )}
              </div>

              <button style={styles.primaryBtn} type="submit">
                Upload
              </button>

              {err ? <div style={styles.err}>{err}</div> : null}
              <div style={styles.tip}>
                Tip: After upload, item stays <b>Pending</b> until you approve it.
              </div>
            </form>
          </div>
        </div>

        {/* List */}
        <div style={styles.card}>
          <div style={styles.cardHead}>
            <div>
              <div style={styles.cardTitle}>Media Items</div>
              <div style={styles.cardSub}>Approve or reject pending items.</div>
            </div>
            {loading ? <Pill tone="pending">Loading…</Pill> : <Pill tone="success">Ready</Pill>}
          </div>

          <div style={styles.cardBody}>
            {err ? <div style={{ ...styles.err, marginBottom: 10 }}>{err}</div> : null}

            {!isMobile ? (
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <Th>Preview</Th>
                      <Th>Type</Th>
                      <Th>Title</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((r) => (
                      <tr key={r.id} style={styles.tr}>
                        <Td>{renderPreview(r)}</Td>
                        <Td>
                          <span style={styles.mono}>{r.media_type}</span>
                        </Td>
                        <Td>
                          <div style={{ fontWeight: 950, color: "#0f172a" }}>{r.title_en || "—"}</div>
                          <div style={styles.muted}>{r.title_kn || ""}</div>
                        </Td>
                        <Td>
                          <StatusPill status={r.status} />
                        </Td>
                        <Td>
                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            {r.status === "pending" ? (
                              <>
                                <button style={styles.approveBtn} onClick={() => approve(r.id)}>
                                  ✓ Approve
                                </button>
                                <button style={styles.rejectBtn} onClick={() => reject(r.id)}>
                                  ✕ Reject
                                </button>
                              </>
                            ) : (
                              <span style={styles.muted}>—</span>
                            )}

                            {/* ✅ NEW: delete always */}
                            <button style={styles.deleteBtn} onClick={() => deleteOne(r.id)}>
                              🗑 Delete
                            </button>
                          </div>
                        </Td>
                      </tr>
                    ))}
                    {rows.length === 0 ? (
                      <tr>
                        <Td colSpan={5}>
                          <div style={styles.empty}>No items.</div>
                        </Td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={styles.mobileList}>
                {rows.length === 0 ? <div style={styles.empty}>No items.</div> : null}

                {pagedRows.map((r) => (
                  <div key={r.id} style={styles.mobileCard}>
                    <div style={styles.mobileTop}>
                      <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <Pill tone="primary">{String(r.media_type || "").toUpperCase()}</Pill>
                          <StatusPill status={r.status} />
                        </div>
                        <div style={{ fontWeight: 950, color: "#0f172a" }}>{r.title_en || "—"}</div>
                        <div style={styles.muted}>{r.title_kn || ""}</div>
                      </div>
                    </div>

                    <div style={{ marginTop: 10 }}>{renderPreview(r)}</div>

                    <div style={styles.mobileActions3}>
                      {r.status === "pending" ? (
                        <>
                          <button style={{ ...styles.approveBtn, width: "100%" }} onClick={() => approve(r.id)}>
                            ✓ Approve
                          </button>
                          <button style={{ ...styles.rejectBtn, width: "100%" }} onClick={() => reject(r.id)}>
                            ✕ Reject
                          </button>
                        </>
                      ) : null}

                      {/* ✅ NEW */}
                      <button style={{ ...styles.deleteBtn, width: "100%" }} onClick={() => deleteOne(r.id)}>
                        🗑 Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <PaginationBar
              page={page}
              pageSize={pageSize}
              total={rows.length}
              pageCount={pageCount}
              compact={isMobile}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- UI helpers ---------------- */

function Header({ title, subtitle, right }) {
  return (
    <div style={styles.header}>
      <div style={{ minWidth: 0 }}>
        <div style={styles.hTitle}>{title}</div>
        <div style={styles.hSub}>{subtitle}</div>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>{right}</div>
    </div>
  );
}

function Kpi({ tone, label, value }) {
  const t = kpiTheme(tone);
  return (
    <div style={{ ...styles.kpi, background: t.bg, borderColor: t.bd }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
        <div style={{ fontWeight: 950, color: "#fff", opacity: 0.95 }}>{label}</div>
        <div style={{ ...styles.kpiDot, background: t.dot }} />
      </div>
      <div style={styles.kpiValue}>{value}</div>
      <div style={styles.kpiSub}>Media items</div>
      <div style={styles.kpiGlow} />
    </div>
  );
}

function PaginationBar({ page, pageSize, total, pageCount, onPageChange, onPageSizeChange, compact }) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  if (total <= pageSize && pageCount <= 1) {
    return (
      <div style={styles.pager}>
        <div style={styles.pagerLeft}>
          <span style={styles.pagerMeta}>
            Showing {from}-{to} of {total}
          </span>
        </div>
        <div style={styles.pagerRight}>
          <span style={styles.pagerMeta}>Rows</span>
          <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))} style={styles.pagerSelect}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pager}>
      <div style={styles.pagerLeft}>
        <span style={styles.pagerMeta}>
          Showing {from}-{to} of {total}
        </span>
      </div>

      <div style={styles.pagerRight}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" style={{ ...styles.pagerBtn, ...(page <= 1 ? styles.pagerBtnDisabled : null) }} onClick={() => onPageChange(1)} disabled={page <= 1} title="First">
            «
          </button>
          <button type="button" style={{ ...styles.pagerBtn, ...(page <= 1 ? styles.pagerBtnDisabled : null) }} onClick={() => onPageChange(page - 1)} disabled={page <= 1} title="Previous">
            ‹
          </button>

          {!compact ? (
            <span style={styles.pagerMeta}>
              Page <b>{page}</b> / {pageCount}
            </span>
          ) : (
            <span style={styles.pagerMeta}>
              <b>{page}</b>/{pageCount}
            </span>
          )}

          <button type="button" style={{ ...styles.pagerBtn, ...(page >= pageCount ? styles.pagerBtnDisabled : null) }} onClick={() => onPageChange(page + 1)} disabled={page >= pageCount} title="Next">
            ›
          </button>
          <button type="button" style={{ ...styles.pagerBtn, ...(page >= pageCount ? styles.pagerBtnDisabled : null) }} onClick={() => onPageChange(pageCount)} disabled={page >= pageCount} title="Last">
            »
          </button>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={styles.pagerMeta}>Rows</span>
          <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))} style={styles.pagerSelect}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function Pill({ tone = "primary", children }) {
  const t = pillTheme(tone);
  return (
    <span style={{ ...styles.pill, background: t.bg, borderColor: t.bd, color: t.tx }}>
      {children}
    </span>
  );
}

function StatusPill({ status }) {
  const s = String(status || "pending").toLowerCase();
  const t = statusTheme(s);
  return (
    <span style={{ ...styles.pill, background: t.bg, borderColor: t.bd, color: t.tx }}>
      {t.label}
    </span>
  );
}

function Th({ children }) {
  return <th style={styles.th}>{children}</th>;
}
function Td({ children, colSpan }) {
  return (
    <td colSpan={colSpan} style={styles.td}>
      {children}
    </td>
  );
}

/* ---------------- Themes ---------------- */

function kpiTheme(tone) {
  if (tone === "success") return { bg: "linear-gradient(135deg,#10b981,#34d399)", bd: "rgba(16,185,129,0.35)", dot: "rgba(255,255,255,0.55)" };
  if (tone === "pending") return { bg: "linear-gradient(135deg,#f59e0b,#fbbf24)", bd: "rgba(245,158,11,0.35)", dot: "rgba(255,255,255,0.55)" };
  if (tone === "danger") return { bg: "linear-gradient(135deg,#ef4444,#fb7185)", bd: "rgba(239,68,68,0.35)", dot: "rgba(255,255,255,0.55)" };
  return { bg: "linear-gradient(135deg,#4f46e5,#2563eb)", bd: "rgba(37,99,235,0.35)", dot: "rgba(255,255,255,0.55)" };
}

function pillTheme(tone) {
  if (tone === "success") return { bg: "#ecfdf3", bd: "#abefc6", tx: "#067647" };
  if (tone === "pending") return { bg: "#fffaeb", bd: "#fedf89", tx: "#b54708" };
  if (tone === "danger") return { bg: "#fff1f2", bd: "#fecdd3", tx: "#b42318" };
  return { bg: "#eff8ff", bd: "#b2ddff", tx: "#175cd3" };
}

function statusTheme(s) {
  if (s === "approved") return { bg: "#ecfdf3", bd: "#abefc6", tx: "#067647", label: "Approved" };
  if (s === "rejected") return { bg: "#fff1f2", bd: "#fecdd3", tx: "#b42318", label: "Rejected" };
  return { bg: "#fffaeb", bd: "#fedf89", tx: "#b54708", label: "Pending" };
}

/* ---------------- Styles ---------------- */

const styles = {
  page: { width: "100%" },

  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  hTitle: {
    fontSize: "clamp(20px, 2vw, 26px)",
    fontWeight: 950,
    letterSpacing: "-0.4px",
    color: "#0f172a",
    lineHeight: 1.1,
  },
  hSub: {
    marginTop: 6,
    fontSize: 13,
    fontWeight: 750,
    color: "#667085",
    maxWidth: 920,
    lineHeight: 1.35,
  },

  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: 12,
    marginBottom: 12,
  },
  kpi: {
    position: "relative",
    borderRadius: 18,
    padding: 14,
    border: "1px solid",
    overflow: "hidden",
    boxShadow: "0 14px 28px rgba(16,24,40,0.10)",
    minHeight: 92,
  },
  kpiDot: { width: 10, height: 10, borderRadius: 999 },
  kpiValue: {
    marginTop: 10,
    fontSize: 30,
    fontWeight: 950,
    color: "#fff",
    letterSpacing: "-0.5px",
    lineHeight: 1,
  },
  kpiSub: { marginTop: 6, color: "rgba(255,255,255,0.9)", fontWeight: 850, fontSize: 12 },
  kpiGlow: {
    position: "absolute",
    right: -42,
    bottom: -44,
    width: 150,
    height: 150,
    borderRadius: 999,
    background: "rgba(255,255,255,0.18)",
  },

  grid: { display: "grid", gap: 12, alignItems: "start" },

  card: {
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #e6eaf0",
    boxShadow: "0 12px 34px rgba(16,24,40,0.06)",
    overflow: "hidden",
  },
  cardHead: {
    padding: 14,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    borderBottom: "1px solid #eef2f7",
    background: "linear-gradient(180deg,#ffffff,#fbfcfe)",
  },
  cardTitle: { fontSize: 15, fontWeight: 950, color: "#0f172a", letterSpacing: "-0.2px" },
  cardSub: { marginTop: 4, fontSize: 13, fontWeight: 750, color: "#667085", lineHeight: 1.35 },
  cardBody: { padding: 14 },

  label: { fontSize: 12, fontWeight: 900, color: "#475467" },

  select: {
    height: 42,
    borderRadius: 12,
    border: "1px solid #d0d5dd",
    padding: "0 12px",
    fontWeight: 850,
    fontSize: 14,
    background: "#fff",
    outline: "none",
  },

  input: {
    height: 42,
    padding: "0 12px",
    borderRadius: 12,
    border: "1px solid #d0d5dd",
    outline: "none",
    fontSize: 14,
    fontWeight: 800,
    color: "#111827",
    background: "#fff",
  },

  drop: {
    borderRadius: 16,
    border: "1px solid #eef2f7",
    background: "linear-gradient(180deg,#ffffff,#f8fafc)",
    padding: 12,
    display: "grid",
    gap: 10,
  },
  file: {
    width: "100%",
    padding: 10,
    borderRadius: 12,
    border: "1px dashed #cbd5e1",
    background: "#fff",
  },
  fileMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    background: "#fff",
    border: "1px solid #eef2f7",
    borderRadius: 12,
    padding: "8px 10px",
  },

  primaryBtn: {
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    background: "linear-gradient(135deg,#4f46e5,#2563eb)",
    color: "#fff",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 14px 30px rgba(37,99,235,0.16)",
  },

  ghostBtn: {
    height: 40,
    padding: "0 12px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
    color: "#0f172a",
  },

  // ✅ NEW
  dangerBtn: {
    height: 40,
    padding: "0 12px",
    borderRadius: 12,
    border: 0,
    background: "#b42318",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
  },

  approveBtn: {
    height: 36,
    padding: "0 12px",
    borderRadius: 12,
    border: 0,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    background: "linear-gradient(135deg,#10b981,#34d399)",
    boxShadow: "0 12px 24px rgba(16,185,129,0.16)",
  },
  rejectBtn: {
    height: 36,
    padding: "0 12px",
    borderRadius: 12,
    border: 0,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    background: "linear-gradient(135deg,#ef4444,#fb7185)",
    boxShadow: "0 12px 24px rgba(239,68,68,0.16)",
  },

  // ✅ NEW
  deleteBtn: {
    height: 36,
    padding: "0 12px",
    borderRadius: 12,
    border: 0,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    background: "#b42318",
    boxShadow: "0 12px 24px rgba(180,35,24,0.16)",
  },

  err: { color: "#b42318", fontWeight: 850, fontSize: 13 },
  tip: { fontSize: 12, fontWeight: 750, color: "#667085", lineHeight: 1.35 },

  muted: { color: "#667085", fontSize: 12, fontWeight: 750 },
  mono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: 13,
    fontWeight: 850,
    color: "#344054",
  },

  pill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 950,
    fontSize: 12,
    border: "1px solid",
    whiteSpace: "nowrap",
  },

  table: { width: "100%", borderCollapse: "collapse", minWidth: 860 },
  th: { textAlign: "left", padding: 12, borderBottom: "1px solid #e6eaf0", color: "#667085", fontSize: 12, fontWeight: 950 },
  td: { padding: 12, borderBottom: "1px solid #f2f4f7", fontSize: 14, verticalAlign: "top", color: "#0f172a" },
  tr: { transition: "background 140ms ease" },

  previewImg: {
    width: 128,
    height: 72,
    objectFit: "cover",
    borderRadius: 12,
    border: "1px solid #e6eaf0",
    background: "#f2f4f7",
  },
  previewVideo: {
    width: 128,
    height: 72,
    objectFit: "cover",
    borderRadius: 12,
    border: "1px solid #e6eaf0",
    background: "#0b1220",
  },
  previewAudio: { width: 220, maxWidth: "100%" },

  mobileList: { display: "grid", gap: 12 },
  mobileCard: {
    borderRadius: 16,
    border: "1px solid #eef2f7",
    background: "#fff",
    padding: 12,
    boxShadow: "0 10px 24px rgba(16,24,40,0.06)",
  },
  mobileTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 },

  // ✅ NEW: 3-column grid for mobile (approve, reject, delete)
  mobileActions3: { marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 },

  empty: {
    padding: 12,
    borderRadius: 14,
    background: "#f8fafc",
    border: "1px solid #eef2f7",
    fontWeight: 850,
    color: "#667085",
  },

  pager: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: "1px solid #eef2f7",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  pagerLeft: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  pagerRight: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" },
  pagerMeta: { fontSize: 12, fontWeight: 800, color: "#667085" },
  pagerBtn: {
    height: 34,
    width: 36,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    color: "#0f172a",
  },
  pagerBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  pagerSelect: {
    height: 34,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    padding: "0 10px",
    fontWeight: 900,
    background: "#fff",
    outline: "none",
  },
};
